import React, { useLayoutEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useAuth } from "utils/useAuth/useAuth";
import { useSnackbar } from "notistack";
import {
  checkNonMoneyDataForTable,
  trimObject,
  dateTimeFormatter,
} from "utils/utils";
import s from "./PayfacDetails.module.scss";
import { queryPayfacAgents } from "./model";

import FunctionalButtonBar from "components/FunctionalButtonBar/FunctionalButtonBar";
import Spin from "components/Spin/Spin";
import Datatable from "components/Datatable/Datatable";
import Grid from "components/Grid/Grid";
import OutlinedText from "components/Input/OutlinedText/OutlinedText";
import SearchFieldButtonBar from "components/SearchFieldButtonBar/SearchFieldButtonBar";
import Divider from "components/Divider/Divider";
import merchantManagement from "routes/merchantManagement";

const AGENTS_PAGE_LOCALE_KEY = "page.agents.agentslist.";

const SEARCH_FIELD_INIT_STATE = {
  isoAgentId: "",
  firstName: "",
  lastName: "",
  email: "",
};

const PayfacDetailsAgents = ({ payfacId, payfacPrefix, redirectFn }) => {
  const { t } = useTranslation();
  const { logout } = useAuth();
  const { enqueueSnackbar } = useSnackbar();
  const gridId = "PayfacAgentsListGrid";

  const [loading, setLoading] = useState(true);
  const [searching, setSearching] = useState(false);
  const [datasource, setDatasource] = useState({
    data: [],
    page: 0,
    size: 20,
    total: 0,
  });
  const [search, setSearch] = useState(false);
  const [currentPage, setCurrentPage] = useState(0);
  const [pageSize, setPageSize] = useState(20);
  const pageSizeInfoRef = useRef({
    currentpage: 0,
    pagesize: 20,
  });

  const [searchFieldInput, setSearchFieldInput] = useState(
    SEARCH_FIELD_INIT_STATE,
  );
  const [focus, setFocus] = useState({});

  useLayoutEffect(() => {
    let isOutdated = false;
    const getIsOutdated = () => isOutdated;

    if (!payfacId) {
      setLoading(false);
      return;
    }

    initialFetchList(getIsOutdated);

    return () => {
      isOutdated = true;
    };
  }, [payfacId]);

  const applyPayfacFilter = (responseBody) => {
    const list = Array.isArray(responseBody?.data) ? responseBody.data : [];
    const hasPayfacIdField = list.some((item) => item?.payfacId != null);
    if (!hasPayfacIdField) {
      // Some backends already scope /agent by payfac; avoid dropping all rows.
      return {
        ...responseBody,
        data: list,
        total: typeof responseBody?.total === "number" ? responseBody.total : list.length,
      };
    }
    const filtered = list.filter(
      (item) => `${item.payfacId}` === `${payfacId}`,
    );
    return {
      ...responseBody,
      data: filtered,
      total: filtered.length,
    };
  };

  const updateDatasource = (response) => {
    setDatasource(response);
    setCurrentPage(response.page);
    setPageSize(response.size);
    pageSizeInfoRef.current.currentpage = response.page;
    pageSizeInfoRef.current.pagesize = response.size;
  };

  const handleApiResponse = (apiResponse) => {
    if (!apiResponse || !apiResponse?.data) {
      snackbar(t("general.message.networkError"), "error");
      return null;
    }
    switch (apiResponse.status) {
      case 401: {
        console.log("You have no authorization to view the page");
        logout("TOKEN_EXPIRED");
        return null;
      }
      case 403: {
        snackbar(t("general.message.unauthorized"), "error");
        return null;
      }
      default: {
        const responseBody = apiResponse.data;
        if (responseBody.responseCode !== "0000") {
          snackbar(
            t(AGENTS_PAGE_LOCALE_KEY + "fetchFail") + ": " + responseBody.message,
            "error",
          );
          updateDatasource({
            total: 0,
            page: 0,
            size: 10,
            data: [],
          });
          return null;
        }
        return applyPayfacFilter(responseBody);
      }
    }
  };

  const fetchAgents = async ({ payload, getIsOutdated, setLoadingState, isSearch }) => {
    setLoadingState(true);
    const apiResponse = await queryPayfacAgents(payload);
    if (getIsOutdated && getIsOutdated()) return;

    const filteredResponse = handleApiResponse(apiResponse);
    if (filteredResponse) {
      if (filteredResponse.data.length === 0) {
        snackbar(t("general.message.no.result"), "warning");
      }
      updateDatasource(filteredResponse);
    }

    setSearch(!!isSearch);
    setLoadingState(false);
  };

  const initialFetchList = async (getIsOutdated) => {
    const payload = {
      ...(payfacPrefix ? { payfacPrefix } : {}),
      page: pageSizeInfoRef.current.currentpage,
      size: pageSizeInfoRef.current.pagesize,
    };
    await fetchAgents({ payload, getIsOutdated, setLoadingState: setLoading, isSearch: false });
  };

  const handleReset = () => {
    setSearchFieldInput(SEARCH_FIELD_INIT_STATE);
    initialFetchList();
  };

  const handleSearch = async (action) => {
    if (!payfacId) return;

    if (action === "search") {
      setCurrentPage(0);
      pageSizeInfoRef.current.currentpage = 0;
    }
    const params = trimObject({ ...searchFieldInput });
    const payload = {
      ...(payfacPrefix ? { payfacPrefix } : {}),
      page: pageSizeInfoRef.current.currentpage,
      size: pageSizeInfoRef.current.pagesize,
      ...params,
    };
    await fetchAgents({
      payload,
      setLoadingState: setSearching,
      isSearch: true,
    });
  };

  const textInputOnChange = (e, field) => {
    const { value } = e.target;
    setSearchFieldInput((state) => ({ ...state, [field]: value }));
  };

  const updateFocus = (field, isFocused) => {
    setFocus({ [field]: isFocused });
  };

  const snackbar = (message, variant, durationInMs = 2000) => {
    return enqueueSnackbar(message, {
      variant,
      anchorOrigin: {
        vertical: "top",
        horizontal: "center",
      },
      autoHideDuration: durationInMs,
    });
  };

  const navigateToDetail = (data) => {
    const path = "/merchant-management/agents/agents-detail";
    const payload = {
      name:
        merchantManagement
          .find((tab) => tab.tabName === "Agents")
          .page.find((page) => page.path === path).name + data.isoAgentId,
      path: path,
      state: {
        isoAgentId: data.isoAgentId,
        email: data.email,
        status: data.status,
      },
    };
    redirectFn("redirectPage", payload);
  };

  const defaultRender = (data, key) => checkNonMoneyDataForTable(data, key);

  const columns = useMemo(() => {
    const base = [
      { key: "isoAgentId", title: "columnTitle.isoAgentId" },
      { key: "firstName", title: "columnTitle.firstName" },
      { key: "lastName", title: "columnTitle.lastName" },
      { key: "email", title: "columnTitle.email" },
      { key: "role", title: "columnTitle.role" },
      {
        key: "createdDate",
        title: "columnTitle.date",
        render: (data, key) =>
          dateTimeFormatter(defaultRender(data, key)),
      },
      { key: "status", title: "columnTitle.status" },
    ];

    return base.map((column, index) => ({
      ...column,
      title: t(AGENTS_PAGE_LOCALE_KEY + column.title),
      sorter: false,
      render: column.render || defaultRender,
      index,
    }));
  }, [t]);

  const pagination = {
    currentPage: currentPage,
    pageSize: pageSize,
    pageSizeOptions: [10, 20, 30, 40],
  };

  const searchField = () => {
    return (
      <div className={s.searchFieldContainer}>
        <Grid gridId={gridId}>
          {[
            { key: "isoAgentId", label: "search.isoAgentId.label", enter: true },
            { key: "firstName", label: "search.firstName.label", enter: true },
            { key: "lastName", label: "search.lastName.label" },
            { key: "email", label: "search.email.label", enter: true },
          ].map(({ key, label, enter }) => (
            <OutlinedText
              key={key}
              label={t(AGENTS_PAGE_LOCALE_KEY + label)}
              value={searchFieldInput[key]}
              onEnterDownFn={enter ? handleSearch : undefined}
              onchangeFn={(e) => textInputOnChange(e, key)}
              onfocusFn={() => updateFocus(key, true)}
              onblurFn={() => updateFocus(key, false)}
              sxParams={{ textFocus: focus[key] }}
            />
          ))}
        </Grid>
        <SearchFieldButtonBar
          loading={loading || searching}
          resetBtn
          handleReset={handleReset}
          searchBtn
          handleSearch={handleSearch}
        />
        <Divider margin={32} />
      </div>
    );
  };

  return (
    <div className={s.container}>
      <div className={s.contentContainer}>
        {searchField()}
        <FunctionalButtonBar
          loading={loading}
          refreshBtn
          handleRefresh={handleSearch}
        />
        <div
          className={[s.datatableContainer, loading ? s.loadingNow : ""].join(
            " ",
          )}
        >
          {loading ? (
            <Spin />
          ) : (
            <Datatable
              navigateToDetail={navigateToDetail}
              columns={columns}
              datasource={datasource.data}
              total={datasource.total}
              pagination={pagination}
              setCurrentPage={setCurrentPage}
              setPageSize={setPageSize}
              pageSizeInfoRef={pageSizeInfoRef}
              handleSearch={handleSearch}
              search={search}
            />
          )}
          {searching && <Spin type={"search"} />}
        </div>
      </div>
    </div>
  );
};

export default React.memo(PayfacDetailsAgents);
